import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { ChangeEvent, useState } from 'react';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { USERNAME_REGEX } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useCheckActions from '../../../hooks/APIHooks/users/useCheckActions';
import useClientActions from '../../../hooks/APIHooks/users/useClientActions';
import { createClientSchema } from '../../../schemas';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import InputFieldBadge from '../Badges/InputFieldBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import BadgeLoading from '../Loadings/BadgeLoading';
import PasswordRestrictionRules from '../PasswordRestrictionRules';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateClientModal = ({
  handleModalClose,
}: {
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [username, setUsername] = useState<string>('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { getCheckAndSuggestUsername } = useCheckActions(username);
  const { createNewClientHandler } = useClientActions('');

  // handler function to handle create/edit client feature
  const createClientHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      const body = new FormData();

      // mandatory fields
      body.append('username', values.username.toLowerCase());
      body.append('email', values.email);
      body.append('password', values.password);
      body.append('is_active', 'true');
      body.append('created_by', currentUser?.username as string);

      await createNewClientHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Client Created!!');

            handleModalClose();
            queryClient.invalidateQueries({ queryKey: ['client-list'] });
          } else {
            toast.error(res.message || 'Client Creation Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create candidate modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='Create Client' />

      <Formik
        initialValues={{
          username: '',
          email: '',
          password: '',
          confirm_password: '',
        }}
        validationSchema={createClientSchema}
        onSubmit={createClientHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <div>
              <AcademiaInputField
                type='text'
                name='username'
                label='Username'
                placeholder='Enter username here...'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setUsername(e.target.value);
                  props.setFieldValue('username', e.target.value);

                  if (USERNAME_REGEX.test(e.target.value)) {
                    queryClient.invalidateQueries({
                      queryKey: ['check-and-suggest-username', e.target.value],
                    });
                  }
                }}
                isRequired
                badge={
                  getCheckAndSuggestUsername.isLoading ? (
                    <BadgeLoading />
                  ) : getCheckAndSuggestUsername.data &&
                    props.values.username ? (
                    <InputFieldBadge
                      badgeText=''
                      badgeValue={getCheckAndSuggestUsername.data.message}
                      isValid={
                        getCheckAndSuggestUsername.data.message === 'Available'
                      }
                    />
                  ) : null
                }
              />

              <AnimatePresence
                initial={false}
                mode='wait'
                onExitComplete={() => null}
              >
                {getCheckAndSuggestUsername.data?.data ? (
                  <motion.label
                    initial={{ y: -20, opacity: 0, display: 'none' }}
                    animate={
                      getCheckAndSuggestUsername.data.message ===
                      'Already Taken'
                        ? { y: 0, opacity: 1, display: 'block' }
                        : {
                            y: -20,
                            opacity: 0,
                            transitionEnd: { display: 'none' },
                          }
                    }
                    htmlFor={`input-user`}
                    className='block mt-1 text-base-red text-sm font-light'
                  >
                    Suggested:
                    {getCheckAndSuggestUsername?.data?.data.map(
                      (suggestion, index) => (
                        <button
                          key={index}
                          type='button'
                          onClick={() => {
                            setUsername(suggestion);
                            props.setFieldValue('username', suggestion);
                          }}
                          className='font-semibold text-base-cyan  hover:opacity-70 duration-300 mx-1'
                        >
                          <span className='underline'>{suggestion}</span>
                          {index <
                          (getCheckAndSuggestUsername?.data?.data
                            ?.length as number) -
                            1
                            ? ', '
                            : ''}
                        </button>
                      )
                    )}
                    etc.
                  </motion.label>
                ) : null}
              </AnimatePresence>
            </div>

            <AcademiaInputField
              type='email'
              name='email'
              label='Email'
              placeholder='Enter email here...'
              isRequired
            />

            <div>
              <AcademiaInputField
                type='password'
                name='password'
                label='Password'
                placeholder='Enter password here...'
                isRequired
              />

              <PasswordRestrictionRules
                password={props.values.password}
                isTouched={!!props.touched.password}
                error={props.errors.password}
              />
            </div>

            <div className='!mt-1'>
              <AcademiaInputField
                type='password'
                name='confirm_password'
                label='Confirm Password'
                placeholder='Retype password to confirm here...'
                isRequired
              />
            </div>

            <AcademiaButton
              title={
                getCheckAndSuggestUsername.data?.message === 'Already Taken'
                  ? 'Please, provide a valid username!'
                  : undefined
              }
              type='submit'
              variant='premium'
              isLoading={props.isSubmitting}
              isDisabled={
                getCheckAndSuggestUsername.data?.message === 'Already Taken'
              }
              icon={<TbSquareRoundedPlusFilled className='size-6' />}
              fullWidth
            >
              Create Client
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateClientModal;
